import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { useNow } from './useNow'

interface ReminderRow {
  id: string
  conversation_urn: string
  remind_at: string
}

interface Reminder {
  id: string
  conversationUrn: string
  remindAt: number // ms timestamp
}

const DUE_CHECK_INTERVAL = 60_000 // check every 60s

export const remindersKey = (userId: string | undefined): QueryKey => ['reminders', userId ?? 'anon']

export function useReminders() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = remindersKey(user?.id)

  const query = useQuery<Reminder[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('followups')
        .select('id, conversation_urn, remind_at')
        .eq('completed', false)
        .order('remind_at', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []).map((r: ReminderRow) => ({
        id: r.id,
        conversationUrn: r.conversation_urn,
        remindAt: new Date(r.remind_at).getTime(),
      }))
    },
  })

  // Realtime invalidation is handled centrally by <RealtimeBridge />.

  const reminders = query.data ?? []

  const reminderMap = useMemo(() => {
    const map = new Map<string, Reminder>()
    for (const r of reminders) map.set(r.conversationUrn, r)
    return map
  }, [reminders])

  const reminderUrns = useMemo(
    () => new Set(reminders.map((r) => r.conversationUrn)),
    [reminders],
  )

  // Due count ticks every minute regardless of refetch — wall clock, not data.
  // `useNow` pools the timer across every hook that wants a minute-tick, so
  // mounting useReminders twice (or alongside future wall-clock consumers)
  // doesn't fan out into N separate `setInterval`s.
  const now = useNow(DUE_CHECK_INTERVAL)
  const dueCount = useMemo(
    () => reminders.filter((r) => r.remindAt <= now).length,
    [reminders, now],
  )

  const setReminderMut = useMutation({
    mutationKey: ['reminders', 'set'],
    mutationFn: async ({ conversationUrn, remindAt }: { conversationUrn: string; remindAt: Date }) => {
      if (!user) throw new Error('Not authenticated')
      // Read existence from the live TanStack cache, not the render-time
      // `reminderMap`. The memo only updates between renders — if two
      // setReminder calls are fired in the same tick (common from rapid
      // keyboard shortcuts) the second would see a stale "no existing"
      // and perform a fresh INSERT, leaving the user with two rows for
      // the same conversation and a broken clearReminder lookup.
      const current = qc.getQueryData<Reminder[]>(key) ?? []
      const existing = current.find((r) => r.conversationUrn === conversationUrn)
      if (existing) {
        const { error } = await supabase
          .from('followups')
          .update({ remind_at: remindAt.toISOString(), completed: false })
          .eq('id', existing.id)
        if (error) throw new Error(error.message)
        return { id: existing.id, conversationUrn, remindAt }
      }
      // Insert path: capture the DB-assigned id via `.select().single()` so
      // the cache mirrors the real row. Previously we minted a
      // `crypto.randomUUID()` for the cache, then on realtime fan-in the
      // row arrived with a *different* id — the cache briefly held two
      // rows for the same conversation, and clearing one failed because
      // clearReminder looked up the conversation-URN-keyed real id but
      // the effective-now cache still had the temp one. Using the real id
      // from the start closes that gap.
      const { data, error } = await supabase
        .from('followups')
        .insert({
          user_id: user.id,
          conversation_urn: conversationUrn,
          remind_at: remindAt.toISOString(),
        })
        .select('id')
        .single()
      if (error) throw new Error(error.message)
      if (!data) throw new Error('insert returned no row')
      return { id: data.id as string, conversationUrn, remindAt }
    },
    onSuccess: ({ id, conversationUrn, remindAt }) => {
      qc.setQueryData<Reminder[]>(key, (prev) => {
        const without = (prev ?? []).filter((r) => r.conversationUrn !== conversationUrn)
        return [...without, { id, conversationUrn, remindAt: remindAt.getTime() }]
          .sort((a, b) => a.remindAt - b.remindAt)
      })
    },
  })

  const clearReminderMut = useMutation({
    mutationKey: ['reminders', 'clear'],
    mutationFn: async (conversationUrn: string) => {
      if (!user) throw new Error('Not authenticated')
      // See setReminder above — read from cache so a clear fired right
      // after a set sees the freshly-inserted row rather than the
      // render-time memo's snapshot.
      const current = qc.getQueryData<Reminder[]>(key) ?? []
      const existing = current.find((r) => r.conversationUrn === conversationUrn)
      if (!existing) return { conversationUrn }
      const { error } = await supabase
        .from('followups')
        .update({ completed: true })
        .eq('id', existing.id)
      if (error) throw new Error(error.message)
      return { conversationUrn }
    },
    onSuccess: ({ conversationUrn }) => {
      qc.setQueryData<Reminder[]>(key, (prev) =>
        (prev ?? []).filter((r) => r.conversationUrn !== conversationUrn),
      )
    },
  })

  return {
    reminders,
    reminderUrns,
    loading: !!user && query.isPending,
    dueCount,
    // Return the raw mutateAsync — callers can .catch() for toast, and
    // unhandled rejections surface in the console for triage. (Previous
    // try/catch swallowed errors silently.)
    setReminder: (conversationUrn: string, remindAt: Date) =>
      setReminderMut.mutateAsync({ conversationUrn, remindAt }),
    clearReminder: (conversationUrn: string) =>
      clearReminderMut.mutateAsync(conversationUrn),
    isReminderSet: (conversationUrn: string) => reminderMap.has(conversationUrn),
    getReminderTime: (conversationUrn: string) => reminderMap.get(conversationUrn)?.remindAt || null,
  }
}
